import React from "react";
import type { IconType } from "react-icons";
import {
  MdAudioFile,
  MdCode,
  MdDescription,
  MdFolderZip,
  MdImage,
  MdInsertDriveFile,
  MdPictureAsPdf,
  MdSlideshow,
  MdTableChart,
  MdTextSnippet,
  MdVideoFile,
} from "react-icons/md";
import { resolveAttachmentFileKind, type AttachmentFileKind } from "./attachmentFileKind";

function iconForKind(kind: AttachmentFileKind): IconType {
  switch (kind) {
    case "image":
      return MdImage;
    case "video":
      return MdVideoFile;
    case "audio":
      return MdAudioFile;
    case "pdf":
      return MdPictureAsPdf;
    case "word":
      return MdDescription;
    case "sheet":
      return MdTableChart;
    case "slides":
      return MdSlideshow;
    case "archive":
      return MdFolderZip;
    case "text":
      return MdTextSnippet;
    case "code":
      return MdCode;
    default:
      return MdInsertDriveFile;
  }
}

function toneClass(kind: AttachmentFileKind): string {
  switch (kind) {
    case "image":
      return "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400";
    case "video":
      return "bg-violet-500/15 text-violet-600 dark:text-violet-400";
    case "audio":
      return "bg-amber-500/15 text-amber-600 dark:text-amber-400";
    case "pdf":
      return "bg-red-500/15 text-red-600 dark:text-red-400";
    case "word":
      return "bg-blue-500/15 text-blue-600 dark:text-blue-400";
    case "sheet":
      return "bg-green-600/15 text-green-700 dark:text-green-400";
    case "slides":
      return "bg-orange-500/15 text-orange-600 dark:text-orange-400";
    case "archive":
      return "bg-yellow-500/20 text-yellow-800 dark:text-yellow-300";
    case "text":
      return "bg-slate-500/15 text-slate-600 dark:text-slate-300";
    case "code":
      return "bg-cyan-500/15 text-cyan-700 dark:text-cyan-400";
    default:
      return "bg-gray-500/15 text-gray-600 dark:text-gray-400";
  }
}

export type AttachmentTypeIconProps = {
  contentType?: string | null;
  fileName?: string | null;
  size?: "compact" | "sm" | "md" | "lg";
  className?: string;
};

export default function AttachmentTypeIcon({
  contentType = "",
  fileName = "",
  size = "md",
  className = "",
}: AttachmentTypeIconProps) {
  const kind = resolveAttachmentFileKind(contentType || "", fileName || "");
  const Icon = iconForKind(kind);
  const box = toneClass(kind);
  const outer =
    size === "sm"
      ? "h-10 w-10"
      : size === "compact"
        ? "h-12 w-12"
        : size === "lg"
          ? "h-20 w-20"
          : "h-14 w-14";
  const inner =
    size === "sm"
      ? "h-5 w-5"
      : size === "compact"
        ? "h-6 w-6"
        : size === "lg"
          ? "h-10 w-10"
          : "h-7 w-7";
  const radius = size === "compact" ? "rounded-lg" : "rounded-xl";

  return (
    <span
      className={`inline-flex ${outer} shrink-0 items-center justify-center ${radius} shadow-sm ring-1 ring-gray-200/80 dark:ring-white/10 ${box} ${className}`}
    >
      <Icon className={`${inner} block shrink-0 fill-current`} aria-hidden />
    </span>
  );
}
